import {
  FulfillmentHookValue,
  SelectedFulfillmentType,
} from '@/contexts/new-order-actions-context-types';
import { OrderAvailability } from '@domain/fulfillment-settings/blocs/order-availability';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNewOrderContext } from '@/contexts/new-order-context';

/*
 * Don't check draft order dates or any saved order dates for inbounds.
 * Only hydrate using the next available fulfillment date.
 * The context will not remount when the selected restaurant changes, so we
 * manually track the last restaurant that was hydrated and re-run if it
 * changes.
 */
export const useInboundFulfillment = () => {
  const {
    availableOrderFulfillments,
    hasResponse,
    restaurantUUID,
    supplierTimezone,
  } = useNewOrderContext();
  const { selectedRestaurantRelationship } = useAuthenticatedUserContext();

  const [
    fulfillmentHydratedForRestaurant,
    setFulfillmentHydratedForRestaurant,
  ] = useState<string | null>(null);
  const fulfillmentHydratedForRestaurantRef = useRef<string>(null);

  const [selectedFulfillment, setSelectedFulfillment] =
    useState<SelectedFulfillmentType | null>(null);

  useEffect(() => {
    if (
      !hasResponse ||
      !restaurantUUID ||
      fulfillmentHydratedForRestaurantRef.current === restaurantUUID
    ) {
      return;
    }

    const timezoneMap = {
      restaurant: selectedRestaurantRelationship.timezone,
      supplier: supplierTimezone,
    };

    const defaultNewOrderFulfillment = new OrderAvailability(
      availableOrderFulfillments,
      timezoneMap,
      null,
    );

    const nextFulfillmentDate =
      defaultNewOrderFulfillment.nextFulfillmentDayMoment.toISOString();
    const nextFulfillmentType = defaultNewOrderFulfillment.fulfillmentType;

    // Only update local state on mount to avoid updating drafts incorrectly.
    setSelectedFulfillment({
      date: nextFulfillmentDate,
      fulfillmentSettingsUUID:
        defaultNewOrderFulfillment.selectedFulfillmentSettingsUUID,
      timeslot: null,
      type: nextFulfillmentType,
    });

    fulfillmentHydratedForRestaurantRef.current = restaurantUUID;
    setFulfillmentHydratedForRestaurant(restaurantUUID);
  }, [
    availableOrderFulfillments,
    hasResponse,
    restaurantUUID,
    selectedRestaurantRelationship.timezone,
    supplierTimezone,
  ]);

  const resetFulfillmentToNextAvailable = useCallback(() => {
    const timezoneMap = {
      restaurant: selectedRestaurantRelationship.timezone,
      supplier: supplierTimezone,
    };
    if (restaurantUUID && fulfillmentHydratedForRestaurantRef.current) {
      const defaultNewOrderFulfillment = new OrderAvailability(
        availableOrderFulfillments,
        timezoneMap,
        null,
      );
      setSelectedFulfillment({
        date: defaultNewOrderFulfillment.nextFulfillmentDayMoment.toISOString(),
        fulfillmentSettingsUUID:
          defaultNewOrderFulfillment.selectedFulfillmentSettingsUUID,
        timeslot: null,
        type: defaultNewOrderFulfillment.fulfillmentType,
      });
    }
  }, [
    availableOrderFulfillments,
    restaurantUUID,
    selectedRestaurantRelationship.timezone,
    supplierTimezone,
  ]);

  return useMemo<FulfillmentHookValue>(
    () => ({
      fulfillmentHydrated: fulfillmentHydratedForRestaurant === restaurantUUID,
      // Inbounds never create a cart, so there is no new-cart starting point to offer.
      nextAvailableFulfillment: null,
      resetFulfillmentToNextAvailable,
      selectedFulfillment,
      selectFulfillment: setSelectedFulfillment,
    }),
    [
      fulfillmentHydratedForRestaurant,
      restaurantUUID,
      resetFulfillmentToNextAvailable,
      selectedFulfillment,
    ],
  );
};
